import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { resolveTableFromSlug } from "@/data/restaurant";
import { scopedKey, readJSON, writeJSON, makeId } from "@/lib/storage";

const SessionContext = createContext(null);
const storageKey = () => scopedKey("session");

// Opened without an NFC/QR entry: fall back to the demo table (as before),
// with its own session so orders/ratings still have a visit to belong to. The
// generated session is persisted right away so a reload keeps the same visit
// (and the same rating targets). Local mock only — not backend session auth.
export function loadOrCreateSession() {
  const stored = readJSON(storageKey(), null);
  if (stored?.sessionId) return stored;
  const created = { ...resolveTableFromSlug("demo-table-12"), sessionId: makeId("sess") };
  writeJSON(storageKey(), created);
  return created;
}

// Mock table session. A session id ties orders, waiter calls and ratings to one
// visit; re-tapping the same table tag keeps the session, a new table starts one.
export function SessionProvider({ children }) {
  const [table, setTable] = useState(loadOrCreateSession);

  const setTableFromSlug = useCallback((slug) => {
    const resolved = resolveTableFromSlug(slug);
    const prev = readJSON(storageKey(), null);
    const sessionId = prev?.slug === resolved.slug && prev?.sessionId ? prev.sessionId : makeId("sess");
    const next = { ...resolved, sessionId };
    setTable(next);
    writeJSON(storageKey(), next);
    return next;
  }, []);

  const value = useMemo(
    () => ({ table, sessionId: table?.sessionId || null, setTableFromSlug }),
    [table, setTableFromSlug]
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within SessionProvider");
  return ctx;
}
